import { DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Descriptions, Form, Input, Layout, message, Modal, Pagination, Popconfirm, Row, Select, Space, Spin, Table } from "antd";
import { User } from "../../../../services/users/user";
import { ColumnsType } from "antd/es/table";
import { createRef, useEffect, useRef, useState } from "react";
import FileUpload, { FileUploadRef } from "../../../../components/file-input/FileUpload";
import { FilterRequest, OrderTypes, OrderValue } from "../../../../utils/Filters/FilterModels";
import FilterHelper from "../../../../utils/Filters/FilterHelper";
import axiosInstance from "../../../../api/axiosInstance";
import { Blog } from "../../../../services/blog/blog";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import '../../../../styles/ckeditor-custom.scss';
import { useNavigate } from "react-router-dom";

const { TextArea } = Input;
const { Content } = Layout;
const API_URL = process.env.REACT_APP_API_URL;

const BlogManagement = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [tableHeight, setTableHeight] = useState(500);
    const [data, setData] = useState<Blog[]>([]);
    const [searchText, setSearchText] = useState("");
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filter, setFilter] = useState<FilterRequest>(new FilterRequest({
        page: 1,
        limit: 10,
        keyword: '',
        summary: '',
        filters: [],
        orders: [
            new OrderValue({ propertyName: 'createdDate', type: OrderTypes.Desc, index: 0 }),
            new OrderValue({ propertyName: 'status', type: OrderTypes.Asc, index: 0 })
        ],
        isFull: false,
        tree: false,
    }));

    useEffect(() => {
        const width = window.innerWidth;
        if (width <= 1366) setTableHeight(250);
        else setTableHeight(500);
    }, []);

    useEffect(() => {
        const params = FilterHelper.getParamWithFilter(filter);
        fetchTournaments(params);
    }, []);


    const fetchTournaments = async (paramValues?: any) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/blogs", {
                params: paramValues ?? filter,
            });
            const items = response.data?.list || [];
            setTotalRecords(response.data?.totalRecords || 0);
            const formattedData: Blog[] = items.map((item: any) => ({
                blogName: item.blogName,
                blogCode: item.blogCode,
                blogID: item.blogID,
                isActive: item.isActive,
                description: item.description
            }));

            setData(formattedData);
        } catch (error: any) {
            console.error("Lỗi khi lấy danh sách giải đấu:", error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
        const newFilter = new FilterRequest({
            ...filter,
            page,
            limit: pageSize,
        });
        setFilter(newFilter);
        const params = FilterHelper.getParamWithFilter(newFilter);
        fetchTournaments(params);
    };
    const handleCreateBlog = () => {
        navigate("blog-detail-management/new");
    }

    const handleRefreshBlog = () => {
        const params = FilterHelper.getParamWithFilter(filter);
        fetchTournaments(params);
    }
    const handleDelete = async (id: string) => {
        try {
            await axiosInstance.put(`/blogs/delete-blog`, { blogIds: [id] });
            message.success("Xoá blog thành công!");
            setData((prev) => prev.filter((item) => item.blogID !== id));
        } catch (error: any) {
            message.error(error?.response?.systemMessage);
        }
    };

    const handleEditBlog = (record: Blog) => {
        navigate(`blog-detail-management/${record.blogID}`);
    };

    const columns: ColumnsType<any> = [
        { title: "Mã blog", dataIndex: "blogCode", key: "blogCode", width: 200 },
        { title: "Tên Blog", dataIndex: "blogName", key: "blogName" },
        { title: "Mô tả", dataIndex: "description", key: "description" },
        {
            title: "Được sử dụng", align: 'center', dataIndex: "isActive", key: "isActive", width: 150, render: (text: any, record: any) => { let color = record.isActive === false ? "#F42121" : "#07BD74"; return <span style={{ color }}>{(text === false ? "Ngừng hoạt động" : "Đang hoạt động")}</span> }
        },
        {
            title: "Hành động",
            key: "action",
            width: 150,
            render: (_: any, record: Blog) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEditBlog(record)} />
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa?"
                        onConfirm={() => handleDelete(record.blogID as string)}
                        okText="Có"
                        cancelText="Không"
                    >
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const filteredData = data.filter(
        (blog) =>
            blog?.blogName?.toLowerCase().includes(searchText)
    );

    return (
        <Content style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#fff",
                    zIndex: 999,
                    padding: "16px",
                    borderBottom: "1px solid #ddd",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
            >
                <Space style={{ width: "100%" }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{ backgroundColor: "#F48100", borderColor: "#F48100" }}
                        onClick={handleCreateBlog}
                    >
                        Thêm mới
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshBlog}
                    >
                        Làm mới
                    </Button>
                </Space>
            </div>
            <div style={{ flex: 1, overflow: "auto", marginTop: "16px" }}>
                {loading ? (
                    <Spin fullscreen tip="Đang tải dữ liệu..." size="large" />
                ) : (
                    <Table
                        columns={columns}
                        onRow={(record) => ({
                            onClick: (e) => {
                                const actionColumnIndex = columns.findIndex((col) => col.key === 'action');
                                const clickedColumn = (e.target as HTMLElement).closest('td');

                                if (clickedColumn && clickedColumn.cellIndex === actionColumnIndex) {
                                    return;
                                }
                            },
                        })}
                        dataSource={filteredData}
                        pagination={false}
                        scroll={{ y: tableHeight }}
                    />
                )}
            </div>
            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "#fff",
                    paddingTop: "10px",
                    borderTop: "-1px solid #ddd",
                    boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
                    textAlign: "center",
                }}
            >
                <Pagination
                    current={filter.page}
                    pageSize={filter.limit}
                    total={totalRecords}
                    onChange={handlePaginationChange}
                    showTotal={(total, range) =>
                        `Hiển thị ${range[0]} - ${range[1]} của ${total} bản ghi`
                    }
                />
            </div>
        </Content>
    )
}
export default BlogManagement